import React from 'react';
import { Link } from 'react-router-dom'; // Use Link for internal navigation
import {
  ArrowRightIcon, GiftIcon, QuestionMarkCircleIcon, MapPinIcon,
  InformationCircleIcon, BookOpenIcon, UsersIcon, AcademicCapIcon,
  ShoppingCartIcon, LightBulbIcon, ShieldCheckIcon, DocumentTextIcon
} from '@heroicons/react/24/outline';
// ✅ 1. Import social media icons from the new library
import { FaFacebookF, FaInstagram, FaTiktok } from 'react-icons/fa';

export default function Footer() {
  const brandColor = '#e46809';

  // ✅ 2. Updated links object to use real paths
  const footerLinks = {
    about: [
      { text: 'How It Works', href: '/how-it-works', icon: <ArrowRightIcon className="w-4 h-4" /> },
      { text: 'Supported States', href: '/supported-states', icon: <MapPinIcon className="w-4 h-4" /> },
      { text: 'Help Center', href: '/help', icon: <QuestionMarkCircleIcon className="w-4 h-4" /> },
      { text: 'About Us', href: '/about', icon: <InformationCircleIcon className="w-4 h-4" /> }
    ],
    gifting: [
      { text: 'Start a Campaign', href: '/login', state: { defaultTab: 'signup', role: 'recipient' }, icon: <BookOpenIcon className="w-4 h-4" /> },
      { text: 'Browse Campaigns', href: '/campaigns', icon: <GiftIcon className="w-4 h-4" /> },
      { text: 'For Organizations', href: '/organizations', icon: <UsersIcon className="w-4 h-4" /> }
    ],
    affiliations: [
      { text: 'Accesstech Store', href: 'https://accesstechstore.com', external: true, icon: <ShoppingCartIcon className="w-4 h-4" /> },
      { text: 'Accesstech Innovations', href: 'https://accesstechinnovation.org', external: true, icon: <LightBulbIcon className="w-4 h-4" /> },
      { text: 'Accesstech Academy', href: 'https://accesstechacademy.com', external: true, icon: <AcademicCapIcon className="w-4 h-4" /> }
    ]
  };

  // ✅ 3. Updated social links with icons
  const socialLinks = [
    { name: 'Facebook', href: '#', icon: <FaFacebookF size={20} /> },
    { name: 'Instagram', href: '#', icon: <FaInstagram size={20} /> },
    { name: 'TikTok', href: '#', icon: <FaTiktok size={20} /> }
  ];

  return (
    <footer className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 py-16">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          
          <div className="flex flex-col items-center md:items-start">
            <Link to="/">
              <img 
                src="/assets/logos/logo.svg"
                alt="GiveTech totheBlind Logo"
                className="h-20 w-auto mb-4"
                title="GiveTech totheBlind - Homepage"
              />
            </Link>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center md:text-left">
              Empowering communities through thoughtful gifting.
            </p>
          </div>

          {Object.entries(footerLinks).map(([key, links]) => (
            <div key={key}>
              <h3 className="text-base font-bold uppercase tracking-wider mb-4" style={{ color: brandColor }}>
                {key}
              </h3>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.text} className="flex items-start">
                    <span className="mr-2 mt-0.5" style={{ color: brandColor }}>{link.icon}</span>
                    {/* ✅ 4. Logic to handle both internal Links and external <a> tags */}
                    {link.external ? (
                      <a href={link.href} target="_blank" rel="noopener noreferrer" className="text-gray-600 dark:text-gray-400 hover:text-orange-500 transition-colors text-sm">
                        {link.text}
                      </a>
                    ) : (
                      <Link to={link.href} state={link.state} className="text-gray-600 dark:text-gray-400 hover:text-orange-500 transition-colors text-sm">
                        {link.text}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div>
            <h3 className="text-base font-bold uppercase tracking-wider mb-4" style={{ color: brandColor }}>Connect</h3>
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <a key={social.name} href={social.href} 
                  className="text-gray-500 hover:text-orange-500 transition-colors"
                  title={`Follow us on ${social.name}`}
                  target="_blank" rel="noopener noreferrer"
                >
                  <span className="sr-only">{social.name}</span>
                  {social.icon}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 mt-12 pt-8 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            &copy; {new Date().getFullYear()} GiveTech totheBlind. All rights reserved.
          </p>
          <div className="flex justify-center flex-wrap gap-x-6 gap-y-2 mt-4">
            <Link to="/privacy-policy" className="flex items-center text-gray-500 hover:text-orange-500 text-sm">
              <ShieldCheckIcon className="w-4 h-4 mr-1" style={{ color: brandColor }} /> Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="flex items-center text-gray-500 hover:text-orange-500 text-sm">
              <DocumentTextIcon className="w-4 h-4 mr-1" style={{ color: brandColor }} /> Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}